import flatMap from 'lodash/flatMap';
import flatten from 'lodash/flatten';
import orderBy from 'lodash/orderBy';
import uniqBy from 'lodash/uniqBy';

import * as React from 'react';
import isEqual from 'react-fast-compare';
import {
    addSeatToCart, flagQuantityAsModified, maxTicketsThresholdReached, removeSeatFromCart,
    resetflexTicketsThreshold, setCartUpdated, updateFlexMessaging
} from '../../../cart/actions/cart-actions';
import { ItemType } from '../../../classes/item-type';
import { PriceType } from '../../../classes/price-type';
import { CartSeat, Seat, SeatPriceQuantity, SeatPricing, SeatZone } from '../../../classes/seat';
import { SeatingOption } from '../../../classes/seating-option';
import { ZoneUpsell } from '../../../classes/zone-upsell';
import { IAdditionalSeatingOptionModel } from '../../../filter/reducers/filter-reducers';
import { CartHelpers } from '../../../helpers/cart-helpers';
import isMobile from '../../../helpers/is-mobile';
import { IExchangeSettingsModel } from '../../../reducers/exchange-reducer';
import { IAppSettingsModel } from '../../../reducers/fetch-settings-reducer';
import { checkSpecialSeatingSeatLimit, checkUnlockSeatConditions, openSeatmapCart, resetZoneUpsellCount, selectPriceTypeQuantities } from '../../actions/seatmap-actions';
import { isSingleClick } from '../../helpers/seatmap-helpers';
import { ISeatMapModel } from '../../reducers/seatmap-reducer';
import SeatingInfo from './seating-info';



interface ISeatingInfoContainerProps {
    additionalSeatingOptionModel: IAdditionalSeatingOptionModel;
    appSettingsModel: IAppSettingsModel;
    cartSeats: Array<CartSeat>;
    cartUpdated: boolean;
    selectedPriceTypes: Array<PriceType>;
    activeSeats: Array<Seat>;
    levelPricing: Array<SeatZone>;
    dispatch: any;
    viewFromSeatModalActive: boolean;
    maxQuantityPriceTypeError: boolean;
    maxQuantitySeatingOptionError: boolean;
    flexQuantityPriceTypeError: boolean;
    flexMinQuantityError: boolean;
    flexMaxQuantityErrorAllTickets: boolean;
    flexMaxQuantityErrorPriceType: boolean;
    quantityHasBeenModified: boolean;
    seatmapModel: ISeatMapModel;
    isGAZone?: boolean;
    exchangeModel: IExchangeSettingsModel;
    zoneUpsell: ZoneUpsell;
    seats: Array<Seat>;
    onCloseZoneUpsell: () => any;
    onToggleSeatingInfoOpen: () => any;
    selectedPriceTypeQuantities: Array<SeatPriceQuantity>;
    hidingUpsell: boolean;

    showOverlay: boolean;
    closeOverlays: () => void;
    showSeatingInfo: boolean;

}

interface ISeatingInfoContainerState {
    selectedSeat: boolean;
    confirmedSeat: boolean,
    selectedPriceQuantities: Array<SeatPriceQuantity>,
    selectedSeatingOptions: Array<SeatingOption>,
    needPriceTypeError: boolean,
    needSeatingOptionError: boolean,
    previousSelectedSeats: Array<Seat>,
    noMoreExchangeableTicketsError: boolean,
    selectedSeatsPreferenceUpdated: boolean
}

/**
 * Return boolean based on whether or not the input seat has any option groups attached.
 * @param seat
 */
class SeatingInfoContainer extends React.Component<ISeatingInfoContainerProps, ISeatingInfoContainerState>{
    static defaultProps = {
        isGAZone: false
    }

    shouldComponentUpdate(nextProps: ISeatingInfoContainerProps, nextState) {
        const oldValue = {
            activeSeats: this.props.activeSeats,
            additionalSeatingOptionModel: this.props.additionalSeatingOptionModel,
            cartSeats: this.props.cartSeats,
            cartUpdated: this.props.cartUpdated,
            flexMaxQuantityErrorAllTickets: this.props.flexMaxQuantityErrorAllTickets,
            flexMaxQuantityErrorPriceType: this.props.flexMaxQuantityErrorPriceType,
            flexMinQuantityError: this.props.flexMinQuantityError,
            flexQuantityPriceTypeError: this.props.flexQuantityPriceTypeError,
            maxQuantityPriceTypeError: this.props.maxQuantityPriceTypeError,
            maxQuantitySeatingOptionError: this.props.maxQuantitySeatingOptionError,
            selectedPriceTypes: this.props.selectedPriceTypes,
            viewFromSeatModalActive: this.props.viewFromSeatModalActive,
            requestingSeatView: this.props.seatmapModel.requestingSeatView,
            showSeatingInfo: this.props.showSeatingInfo,
            confirmedAttestation: this.props.seatmapModel.confirmedAttestation
        };

        const newValue = {
            activeSeats: nextProps.activeSeats,
            additionalSeatingOptionModel: nextProps.additionalSeatingOptionModel,
            cartSeats: nextProps.cartSeats,
            cartUpdated: nextProps.cartUpdated,
            flexMaxQuantityErrorAllTickets: nextProps.flexMaxQuantityErrorAllTickets,
            flexMaxQuantityErrorPriceType: nextProps.flexMaxQuantityErrorPriceType,
            flexMinQuantityError: nextProps.flexMinQuantityError,
            flexQuantityPriceTypeError: nextProps.flexQuantityPriceTypeError,
            maxQuantityPriceTypeError: nextProps.maxQuantityPriceTypeError,
            maxQuantitySeatingOptionError: nextProps.maxQuantitySeatingOptionError,
            selectedPriceTypes: nextProps.selectedPriceTypes,
            viewFromSeatModalActive: nextProps.viewFromSeatModalActive,
            requestingSeatView: nextProps.seatmapModel.requestingSeatView,
            showSeatingInfo: nextProps.showSeatingInfo,
            confirmedAttestation: nextProps.seatmapModel.confirmedAttestation
        };

        return !isEqual(oldValue, newValue) || !isEqual(this.state, nextState);
    }

    private get hasConfirmPrompt(): boolean {
        return this.props.activeSeats[0].seatStyleCulture.confirmPrompt !== null;
    }

    private get isInCart(): boolean {
        if (this.props.cartSeats.length === 0)
            return false;
        else {
            var isInCart = this.props.cartSeats.some(cartSeat => this.props.activeSeats.some(seat => cartSeat.seatId === seat.seatId));
            return isInCart;
        }
    }

    //should only be for single seats not GA zones
    private getSelectedSeatingOptionFromFilters(props: ISeatingInfoContainerProps) {
        const selectedAdditionalSeatingOption = flatten(props.additionalSeatingOptionModel.additionalSeatingOptions
            .map(group => group.options)).filter(option => option.selected);

        const selectedSeatingOptionForSeat =
            props.activeSeats[0].seatingOptionGroup !== null ?
                flatten(props.activeSeats[0].seatingOptionGroup.options
                    .filter(seatingOption => selectedAdditionalSeatingOption.map(option => option.id).indexOf(seatingOption.id) >= 0)).map(option => {
                        option.selected = true;
                        return option;
                    }) : [];

        return selectedSeatingOptionForSeat;
    }

    constructor(props) {
        super(props);

        const selectedOptions = this.getSelectedSeatingOptionFromFilters(props);

        this.state = {
            selectedSeat: false,
            confirmedSeat: false,
            selectedPriceQuantities: this.getSelectedPrices(props),
            selectedSeatingOptions: selectedOptions,
            needPriceTypeError: false,
            needSeatingOptionError: selectedOptions.length === 0 && this.seatContainsSeatingOptions(props.activeSeats[0]),
            previousSelectedSeats: null,
            noMoreExchangeableTicketsError: false,
            selectedSeatsPreferenceUpdated: false
        };
    }

    componentWillReceiveProps(nextProps: ISeatingInfoContainerProps) {
        const cartSeats = this.getSeatsInCart(nextProps);
        let activeSeatsInCart = [];

        if (cartSeats != null) {
            activeSeatsInCart = uniqBy(cartSeats.map(cartSeat => {
                return {
                    seatPricing: cartSeat.selectedPricing,
                    quantity: cartSeats.filter(cS => cS.selectedPricing.priceTypeId === cartSeat.selectedPricing.priceTypeId).length
                }
            }), selectedSeat => selectedSeat.seatPricing.priceTypeId);
        }

        const newSeat = (this.state.previousSelectedSeats)
            && !this.state.previousSelectedSeats.every(prevSeat => {
                return nextProps.activeSeats.some(activeSeat => activeSeat.seatId === prevSeat.seatId);
            });

        if (this.state.previousSelectedSeats === null || newSeat || nextProps.cartUpdated) {
            let selectedOptions = this.getSelectedSeatingOptionFromFilters(nextProps);
            if (selectedOptions.length === 0) {
                selectedOptions = this.getSelectedSeatingOptions();
            }

            var newSelectedPriceQuantities = [];
            if (this.getSeatsInCart(nextProps) != null) {
                this.props.dispatch(selectPriceTypeQuantities(activeSeatsInCart, nextProps.appSettingsModel.itemType === ItemType.Flex || nextProps.appSettingsModel.itemType === ItemType.Super))
            } else {
                this.props.dispatch(selectPriceTypeQuantities(this.getSelectedPrices(nextProps), nextProps.appSettingsModel.itemType === ItemType.Flex || nextProps.appSettingsModel.itemType === ItemType.Super));
            }
            this.props.dispatch(setCartUpdated(false));

            this.setState({
                selectedSeat: newSeat ? false : this.state.selectedSeat,
                selectedPriceQuantities: this.getSeatsInCart(nextProps) != null ? activeSeatsInCart : this.getSelectedPrices(nextProps),
                selectedSeatingOptions: selectedOptions,
                needSeatingOptionError: selectedOptions.length === 0 && this.seatContainsSeatingOptions(nextProps.activeSeats[0]),
                previousSelectedSeats: nextProps.activeSeats,
            });
        }
    }

    render() {
        return (
            isMobile() ?
                <div className={'seatmap-content-info ' + (this.props.showOverlay ? " filters-open" : "")} >
                    <SeatingInfo {...this.props}
                        isSingleClick={this.isSingleClickProp()}
                        selectedSeatingOptions={this.getSelectedSeatingOptions()}
                        isSelectedSeat={this.state.selectedSeat}
                        hasConfirmPrompt={this.hasConfirmPrompt}
                        isExchange={this.isExchange()}
                        isInCart={this.isInCart}
                        onCancelConfirmClick={this._onCancelConfirmClick.bind(this)}
                        onConfirmSeatClick={this._onConfirmSeatClick.bind(this)}
                        onSelectPricing={this._onSelectPricing}
                        onSelectSeatClick={this._onSelectSeatClick.bind(this)}
                        onSelectSeatDisabledClick={this.onSelectSeatDisabledClick.bind(this)}
                        onSelectSeatInCartClick={this.onSelectSeatInCartClick.bind(this)}
                        onSelectSeatingOption={this._onSelectSeatingOption.bind(this)}
                        onToggleSeatingInfoOpen={this.props.onToggleSeatingInfoOpen.bind(this)}
                        selectedPriceQuantities={this.state.selectedPriceQuantities}
                        needPriceTypeError={this.state.needPriceTypeError}
                        needSeatingOptionError={this.state.needSeatingOptionError}
                        noMoreExchangeableTicketsError={this.state.noMoreExchangeableTicketsError}
                        selectedSeatsPreferenceUpdated={this.state.selectedSeatsPreferenceUpdated}
                        confirmPromptText={this.props.activeSeats[0].seatStyleCulture.confirmPrompt}
                        exchangeTicketsRemaining={this.props.appSettingsModel.exchangeTickets.length - this.props.cartSeats.length}
                        additionalPurchasable={this.props.appSettingsModel.exchangeTickets.length - this.props.cartSeats.length}
                        minQuantityPriceTypeError={this.minQuantityPriceTypeError()}
                        minQuantityPriceTypeSelectedErrorTextReplace={this.checkForMinQuantity()}
                        showSeatingInfo={this.props.showSeatingInfo}
                        selectedPriceTypeQuantities={this.props.selectedPriceTypeQuantities}
                        onCloseZoneUpsell={this.props.onCloseZoneUpsell}
                        seats={this.props.seats}
                        zoneUpsell={this.props.zoneUpsell}
                        hidingUpsell={this.props.hidingUpsell}
                    />
                </div >
                :
                <>
                    <SeatingInfo {...this.props}
                        isSingleClick={this.isSingleClickProp()}
                        selectedSeatingOptions={this.getSelectedSeatingOptions()}
                        isSelectedSeat={this.state.selectedSeat}
                        hasConfirmPrompt={this.hasConfirmPrompt}
                        isExchange={this.isExchange()}
                        isInCart={this.isInCart}
                        onCancelConfirmClick={this._onCancelConfirmClick.bind(this)}
                        onConfirmSeatClick={this._onConfirmSeatClick.bind(this)}
                        onSelectPricing={this._onSelectPricing}
                        onSelectSeatClick={this._onSelectSeatClick.bind(this)}
                        onSelectSeatDisabledClick={this.onSelectSeatDisabledClick.bind(this)}
                        onSelectSeatInCartClick={this.onSelectSeatInCartClick.bind(this)}
                        onSelectSeatingOption={this._onSelectSeatingOption.bind(this)}
                        onToggleSeatingInfoOpen={this.props.onToggleSeatingInfoOpen.bind(this)}
                        selectedPriceQuantities={this.state.selectedPriceQuantities}
                        needPriceTypeError={this.state.needPriceTypeError}
                        needSeatingOptionError={this.state.needSeatingOptionError}
                        noMoreExchangeableTicketsError={this.state.noMoreExchangeableTicketsError}
                        selectedSeatsPreferenceUpdated={this.state.selectedSeatsPreferenceUpdated}
                        confirmPromptText={this.props.activeSeats[0].seatStyleCulture.confirmPrompt}
                        exchangeTicketsRemaining={this.props.appSettingsModel.exchangeTickets.length - this.props.cartSeats.length}
                        additionalPurchasable={this.props.appSettingsModel.exchangeTickets.length - this.props.cartSeats.length}
                        minQuantityPriceTypeError={this.minQuantityPriceTypeError()}
                        minQuantityPriceTypeSelectedErrorTextReplace={this.checkForMinQuantity()}
                        showSeatingInfo={this.props.showSeatingInfo}
                        selectedPriceTypeQuantities={this.props.selectedPriceTypeQuantities}
                        onCloseZoneUpsell={this.props.onCloseZoneUpsell}
                        seats={this.props.seats}
                        zoneUpsell={this.props.zoneUpsell}
                        hidingUpsell={this.props.hidingUpsell}
                    />
                </>
        );
    }

    isSingleClickProp() {
        const zonePrice = this.props.seatmapModel.allSeatPricing.filter(pricing =>
            pricing.zoneId.toString() === this.props.seatmapModel.selectedSeats[0].tessituraSeat.zoneId);

        let selectedPriceTypeIds = this.props.selectedPriceTypes.map(pt => {
            return pt.priceTypeId;
        });
        let availablePrices = [];

        if (zonePrice.length > 0) {
            availablePrices = zonePrice[0].prices;

            //if (!this.props.appSettingsModel) {
            availablePrices = availablePrices.filter(price => {
                return selectedPriceTypeIds.indexOf(+price.priceTypeId) >= 0;
            });
        }
        //}

        return isSingleClick(this.props.appSettingsModel.disableOneClick,
            this.props.appSettingsModel.enableZoneUpsells,
            this.props.appSettingsModel.zoneUpsellEnableForMobile,
            this.props.seatmapModel.selectedSeats,
            this.props.seatmapModel.zoneUpsells,
            this.props.seatmapModel.selectedPriceQuantities,
            availablePrices
        );
    }

    getSelectedSeatingOptions() {
        const searchSeatInCart = this.props.cartSeats.filter(seat => {
            return seat.seatId === this.props.activeSeats[0].seatId;
        });
        const seatInCart = searchSeatInCart.length > 0 ? searchSeatInCart[0] : null;

        return seatInCart != null ? (seatInCart.selectedSeatingOptions !== undefined ? seatInCart.selectedSeatingOptions : []) : this.state.selectedSeatingOptions;
    }

    _onSelectSeatClick() {
        var cart = document.getElementById('cartIcon');
        if (cart != null) {
            cart.classList.remove('grow');
        }
        if (CartHelpers.isSetQuantityPackage(this.props.appSettingsModel)) {
            this.resetFlexTickets();
        }

        if (this.state.selectedPriceQuantities == null) {
            this.setState({
                ...this.state,
                needPriceTypeError: true,
                needSeatingOptionError: this.state.selectedSeatingOptions.length === 0 && this.seatContainsSeatingOptions(this.props.activeSeats[0]),
                noMoreExchangeableTicketsError: this.state.noMoreExchangeableTicketsError
            });
        } else {
            if (!this.hasConfirmPrompt || this.state.confirmedSeat
                // check to see if it has already been attested
                || (this.props.appSettingsModel.showAttestationOnlyOnce && this.props.seatmapModel.confirmedAttestation.indexOf(this.props.activeSeats[0].seatStyleCulture.confirmPrompt) >= 0)) {
                this._onConfirmSeatClick();
            } else {
                this.setState({
                    ...this.state,
                    selectedSeat: true,
                    needSeatingOptionError: this.state.selectedSeatingOptions.length === 0 && this.seatContainsSeatingOptions(this.props.activeSeats[0]),
                });
            }
        }
    }

    onSelectSeatDisabledClick = () => {
        let seatInfoContainer = document.getElementsByClassName('seat-info-container-main')[0] as HTMLElement;
        seatInfoContainer.scrollBy({
            top: seatInfoContainer.scrollHeight,
            left: 0,
            behavior: 'smooth'
        });

        this.setState({
            ...this.state,
            selectedSeat: false,
            needSeatingOptionError: this.state.selectedSeatingOptions.length === 0 && this.seatContainsSeatingOptions(this.props.activeSeats[0]),
        });
    }

    onSelectSeatInCartClick = () => {
        this.props.dispatch(openSeatmapCart(false));
    }

    _onCancelConfirmClick() {
        this.setState({
            ...this.state,
            selectedSeat: false,
            confirmedSeat: false,
            needPriceTypeError: false,
            needSeatingOptionError: this.state.selectedSeatingOptions.length === 0 && this.seatContainsSeatingOptions(this.props.activeSeats[0]),
        });
    }

    _onConfirmSeatClick() {
        if (this.state.selectedPriceQuantities !== null) {
            this.props.dispatch(flagQuantityAsModified());

            let selectedPricings = [...this.state.selectedPriceQuantities.filter(pq => pq.quantity > 0)];

            let priceTypeSeatsInCart =
                selectedPricings.map(pr => {
                    return {
                        priceTypeId: pr.seatPricing.priceTypeId,
                        cartSeats:
                            this.props.cartSeats.filter(seat => pr.seatPricing.priceTypeId === seat.selectedPricing.priceTypeId)
                    };
                });

            let selectedPriceTypesMinMaxQuantities = selectedPricings.map(pr => {
                return {
                    priceTypeId: pr.seatPricing.priceTypeId,
                    minQuantity: -1,
                    maxQuantity:
                        this.props.appSettingsModel.priceTypeSettings.filter(pt => {
                            return pt.priceTypeId === pr.seatPricing.priceTypeId;
                        })[0].clientMaxQuantity
                }
            });

            if (this.props.appSettingsModel.itemType === ItemType.Flex || this.props.appSettingsModel.itemType === ItemType.Super) {
                selectedPriceTypesMinMaxQuantities = selectedPriceTypesMinMaxQuantities.map(minMax => {
                    minMax.maxQuantity = this.props.selectedPriceTypes.filter(pt => pt.priceTypeId === minMax.priceTypeId)[0].selectedQuantity;
                    minMax.minQuantity = this.props.selectedPriceTypes.filter(pt => pt.priceTypeId === minMax.priceTypeId)[0].selectedQuantity;
                    return minMax;
                });
            }

            // the selected seat has an unlock criteria and has a limit based on the number of the unlock seat in the cart
            if (this.props.activeSeats[0].seatingOptionGroup != null && this.props.activeSeats[0].seatingOptionGroup.unlockSeatingOptionGroupId !== null && this.props.activeSeats[0].seatingOptionGroup.seatLimitPerUnlockSeat > 0) {
                var unlockCriteriaSeatsInCart = this.props.cartSeats.filter(cartSeat => cartSeat.seatingOptionGroup.id === this.props.activeSeats[0].seatingOptionGroup.unlockSeatingOptionGroupId);
                var sameUnlockCriteriaSeatsInCart = this.props.cartSeats.filter(cartSeat => cartSeat.seatingOptionGroup.unlockSeatingOptionGroupId !== null
                    && cartSeat.seatingOptionGroup.unlockSeatingOptionGroupId === this.props.activeSeats[0].seatingOptionGroup.unlockSeatingOptionGroupId);

                if (sameUnlockCriteriaSeatsInCart.length >= (unlockCriteriaSeatsInCart.length * this.props.activeSeats[0].seatingOptionGroup.seatLimitPerUnlockSeat)) {
                    this.props.dispatch(maxTicketsThresholdReached(false, this.props.activeSeats[0].seatingOptionGroup.name));
                    this.setState({
                        ...this.state,
                        needSeatingOptionError: this.state.selectedSeatingOptions.length === 0 && this.seatContainsSeatingOptions(this.props.activeSeats[0]),
                        selectedSeat: false
                    });

                    return;
                }
            }

            // the selected seat has a special seating that has a seat limit
            if (!checkUnlockSeatConditions(this.props.activeSeats[0], this.props.cartSeats)) {
                this.props.dispatch(maxTicketsThresholdReached(false, this.props.activeSeats[0].seatingOptionGroup.name));
                this.setState({
                    ...this.state,
                    needSeatingOptionError: this.state.selectedSeatingOptions.length === 0 && this.seatContainsSeatingOptions(this.props.activeSeats[0]),
                    selectedSeat: false
                });

                return;
            }

            if (!checkSpecialSeatingSeatLimit(this.props.activeSeats[0], this.props.cartSeats)) {

                this.props.dispatch(maxTicketsThresholdReached(false, this.props.activeSeats[0].seatingOptionGroup.name));
                this.setState({
                    ...this.state,
                    needSeatingOptionError: this.state.selectedSeatingOptions.length === 0 && this.seatContainsSeatingOptions(this.props.activeSeats[0]),
                    selectedSeat: false
                });

                return;
            }

            var priceTypeCheck = selectedPriceTypesMinMaxQuantities.filter(quantity => quantity.maxQuantity !== 0
                && priceTypeSeatsInCart.filter(pt => pt.priceTypeId === quantity.priceTypeId)[0].cartSeats.length >= Math.min(quantity.maxQuantity, this.props.appSettingsModel.maxTicketQuantity));

            // Error for when in an exchange and no more tickets can be purchased.
            if (this.isExchange() && this.props.cartSeats.length === this.props.appSettingsModel.exchangeTickets.length) {
                this.setState({
                    ...this.state,
                    needSeatingOptionError: this.state.selectedSeatingOptions.length === 0 && this.seatContainsSeatingOptions(this.props.activeSeats[0]),
                    noMoreExchangeableTicketsError: true,
                    selectedSeat: false
                });
                // When the max quantity for a particular price type has been reached.
            }
            else if (priceTypeCheck.length == selectedPriceTypesMinMaxQuantities.length) {

                this.setState({
                    ...this.state,
                    needSeatingOptionError: this.state.selectedSeatingOptions.length === 0 && this.seatContainsSeatingOptions(this.props.activeSeats[0]),
                    selectedSeat: false
                });
                this.props.dispatch(maxTicketsThresholdReached(true, selectedPricings.filter(pr => pr.seatPricing.priceTypeId == priceTypeCheck[0].priceTypeId)[0].seatPricing.priceTypeDescription));

            } else {
                // Success branch.
                var pricesToBeReserved = this.state.selectedPriceQuantities.filter(priceQuantity => priceQuantity.quantity > 0);

                let availableSeats = this.props.activeSeats.filter(seat => seat.tessituraSeat.isAvailable);

                //remove existing seats of the same path in the cart
                let samePathInCartSeats = this.props.cartSeats.filter(cartSeat => cartSeat.svgPathId === availableSeats[0].svgPathId);

                samePathInCartSeats.forEach(samePathCartSeat => {
                    this.props.dispatch(removeSeatFromCart(samePathCartSeat, this.props.selectedPriceTypes, this.props.appSettingsModel.brandId, true));
                });

                let sliceIndex = 0;
                pricesToBeReserved.forEach(priceQuantity => {
                    var seatsToReserve = availableSeats.slice(sliceIndex, sliceIndex + priceQuantity.quantity);

                    if (seatsToReserve.length > 1) {
                        seatsToReserve = seatsToReserve.map(seat => {
                            let cartSeat = seat as CartSeat;
                            cartSeat.isGroupedSeat = true;
                            return cartSeat;
                        });
                    }

                    sliceIndex = sliceIndex + priceQuantity.quantity;

                    seatsToReserve.forEach(seatToReserve => {
                        let cartSeat = seatToReserve as CartSeat;

                        cartSeat.selectedPricing = priceQuantity.seatPricing;
                        cartSeat.selectedSeatingOptions = this.state.selectedSeatingOptions;
                        cartSeat.isGroupedSeat = this.props.isGAZone;

                        this.props.dispatch(addSeatToCart(cartSeat,
                            this.props.appSettingsModel.priceTypeSettings,
                            this.props.appSettingsModel.useOnlyPromoPriceType,
                            this.props.exchangeModel.activeExchangeTickets,
                            this.props.exchangeModel.exchangeTickets));

                        if (this.props.appSettingsModel.itemType === ItemType.Flex || this.props.appSettingsModel.itemType === ItemType.Super) {
                            this.props.dispatch(updateFlexMessaging(+(selectedPricings[0].seatPricing.priceTypeId), this.props.selectedPriceTypes));
                        }
                    });
                });

                this.setState({
                    ...this.state,
                    confirmedSeat: false,
                    selectedSeatsPreferenceUpdated: false,
                    selectedSeat: false
                });

                if (this.props.seatmapModel.seatUpsoldCount > 0) {
                    this.props.dispatch(resetZoneUpsellCount());
                }
            }
            // Error state for when a pricing isn't selected.
        } else {
            this.setState({
                ...this.state,
                needPriceTypeError: true,
                needSeatingOptionError: this.state.selectedSeatingOptions.length === 0 && this.seatContainsSeatingOptions(this.props.activeSeats[0]),
            });
        }
    }

    _onSelectPricing = (priceQuantities: Array<SeatPriceQuantity>) => {

        this.setState({
            ...this.state,
            selectedPriceQuantities: priceQuantities,
            needPriceTypeError: false,
            needSeatingOptionError: this.state.selectedSeatingOptions.length === 0 && this.seatContainsSeatingOptions(this.props.activeSeats[0]),
            selectedSeatsPreferenceUpdated: true
        });

        const { itemType } = this.props.appSettingsModel;

        this.props.dispatch(selectPriceTypeQuantities(priceQuantities, itemType === ItemType.Flex || itemType === ItemType.Super));
    };

    _onSelectSeatingOption(seatingOptions: Array<SeatingOption>, groupId: string) {
        this.setState({
            ...this.state,
            selectedSeatingOptions: seatingOptions,
            needPriceTypeError: false,
            needSeatingOptionError: seatingOptions.length === 0 && this.seatContainsSeatingOptions(this.props.activeSeats[0])
        });
    }

    handleClickOutside = evt => {
        if (this.props.showOverlay) {
            this.props.closeOverlays();
        }
    };

    // Seating info helper functions
    getSelectedPricesFromFilters(props: ISeatingInfoContainerProps): Array<SeatPriceQuantity> {
        const zonePrices = props.levelPricing.filter(pricing =>
            props.activeSeats.map(seat => seat.tessituraSeat.zoneId)
                .some(zoneId => zoneId === pricing.zoneId.toString()));

        const selectedPriceTypeIds = props.selectedPriceTypes.map(pt => {
            return pt.priceTypeId;
        });

        if (props.selectedPriceTypes.length === 1) {
            const selectedPrices = this.sortSeatPricings(flatMap(zonePrices, zone => zone.prices).filter(price => {
                return selectedPriceTypeIds.indexOf(+price.priceTypeId) >= 0;
            }));

            return selectedPrices.length > 0 ? [{ seatPricing: selectedPrices[0], quantity: 1 }] : [];
        } else if (this.isExchange()) {
            const selectedPrices = this.sortSeatPricings(flatMap(zonePrices, zone => zone.prices));

            return selectedPrices.length > 0 ? [{ seatPricing: selectedPrices[0], quantity: 1 }] : [];
        } else if (props.selectedPriceTypes.length === 0) {
            //This should only happen if the user clicked proceed without selecting a price type/quantity
            //so we will return all of the pricing available in the zone
            return this.sortSeatPricings(flatMap(zonePrices, zone => zone.prices))
                .map(selectedPrices => {
                    return { seatPricing: selectedPrices[0], quantity: 1 };
                });
        } else {
            //normal path of having multiple price types and for now we will return all of them
            let selectedPrices = flatMap(zonePrices, zone => zone.prices);

            if (selectedPriceTypeIds.length > 0) {
                selectedPrices = selectedPrices.filter(price => {
                    return selectedPriceTypeIds.indexOf(+price.priceTypeId) >= 0;
                });
            }

            const sortedPrices = this.sortSeatPricings(selectedPrices);

            if (sortedPrices.length == 1) {
                return sortedPrices.map(p => ({ seatPricing: p, quantity: 1 }));
            } else {
                return sortedPrices.map(p => ({ seatPricing: p, quantity: 0 }));
            }
        }
    }

    getSelectedPrices(props: ISeatingInfoContainerProps): SeatPriceQuantity[] {
        return this.getSelectedPricesFromFilters(props);
    }

    getSeatsInCart(props: ISeatingInfoContainerProps): Array<CartSeat> {
        let searchSeatInCart = props.cartSeats.filter(seat => {
            return props.activeSeats.map(activeSeat => activeSeat.seatId).some(seatId => seatId === seat.seatId);
        });

        return searchSeatInCart.length > 0 ? searchSeatInCart : null;
    }

    sortSeatPricings(seatPricings: SeatPricing[]): SeatPricing[] {
        const sorted = orderBy(seatPricings,
            [s => CartHelpers.maxSeatsInCart(s.priceTypeId, this.props.cartSeats, this.props.appSettingsModel.priceTypeSettings),
            s => this.reachedSelectedQuantity(s),
            s => s.isDefaultPriceType,
            s => s.priority > 0,
            s => s.priority,
            ], ['asc', 'asc', 'desc', 'desc', 'asc']);
        return sorted;
    }

    reachedSelectedQuantity(seatPricing: SeatPricing): boolean {
        const selectedPriceTypeMatches = this.props.selectedPriceTypes.filter(pt => pt.priceTypeId == +seatPricing.priceTypeId);
        const seatsInCart = this.props.cartSeats.filter(s => s.selectedPricing.priceTypeId == seatPricing.priceTypeId).length;
        if (selectedPriceTypeMatches.length == 0) {
            return false;
        }
        return seatsInCart >= selectedPriceTypeMatches[0].selectedQuantity;
    }

    isExchange(): boolean {
        return this.props.appSettingsModel.exchangeTickets.length > 0;
    }

    seatContainsSeatingOptions(seat: Seat) {
        return seat.seatingOptionGroup !== null && seat.seatingOptionGroup.options
            .filter(op => op.name != null && op.name.length > 0).length > 0;
    }

    getNewSeatingOptions(currSeatingOptions: SeatingOption[], selectedSeatingOption: SeatingOption) {
        return uniqBy([selectedSeatingOption].concat(currSeatingOptions), 'id');
    }

    resetFlexTickets() {
        //this.props.dispatch(resetmaxTicketsThreshold());
        this.props.dispatch(resetflexTicketsThreshold());
    }

    minQuantityPriceTypeError(): boolean {
        return this.checkForMinQuantity().length > 0
            && this.props.quantityHasBeenModified;
    }

    checkForMinQuantity(): string {
        if (this.state.selectedPriceQuantities != null && this.state.selectedPriceQuantities.length > 0) {
            var minQuantityRequired = this.state.selectedPriceQuantities.filter(pq => pq.quantity > 0 &&
                !CartHelpers.checkPriceTypeForMinQuantities(pq.seatPricing.priceTypeId, this.props.cartSeats, this.props.appSettingsModel.priceTypeSettings));

            if (minQuantityRequired.length > 0) {
                return this.props.appSettingsModel.priceTypeSettings.filter(ps => ps.priceTypeId === minQuantityRequired[0].seatPricing.priceTypeId)[0].title;
            } else
                return "";
        } else {
            return "";
        }
    }
}

export default SeatingInfoContainer;